import React from "react";
import { toast } from "react-toastify";
import CustomToast from "../Components/CustomToast";

/**
 * Modern Premium Notification & Toast Service
 * Implements exact visual specifications from UI Design Guide:
 * - 16px-18px rounded pill container
 * - Soft pastel colored background & subtle matching borders
 * - Circular white badge with colored action icon
 * - Dark high-contrast title + muted subtitle
 * - Auto-dismiss: Success (3s), Error (5s), Warning (5s), Info (4s)
 */
export const notify = {
  /**
   * Success Toast (3s auto-dismiss)
   * @param {string} title
   * @param {string|null} description
   */
  success: (title, description = null) => {
    return toast(
      ({ closeToast }) => (
        <CustomToast
          type="success"
          title={title || "Success"}
          description={description}
          closeToast={closeToast}
        />
      ),
      {
        autoClose: 3000,
        className: "custom-toast-wrapper",
        bodyClassName: "custom-toast-body",
        hideProgressBar: true,
        closeButton: false,
        icon: false
      }
    );
  },

  /**
   * Error Toast (5s auto-dismiss)
   * Sanitizes technical errors into user-friendly messages.
   * @param {string|object} content
   * @param {string|null} description
   */
  error: (content, description = null) => {
    let friendlyTitle = "Error";
    let friendlyDesc = description;

    if (typeof content === "string") {
      friendlyTitle = content;
    } else if (typeof content === "object" && content !== null) {
      friendlyTitle =
        content?.data?.message ||
        content?.message ||
        content?.error ||
        "Action Failed";
    }

    if (
      friendlyTitle &&
      (friendlyTitle.includes("500") ||
        friendlyTitle.includes("Sequelize") ||
        friendlyTitle.includes("Internal Server"))
    ) {
      friendlyTitle = "Service Temporarily Unavailable";
      friendlyDesc = "Please try again in a few moments.";
    }

    return toast(
      ({ closeToast }) => (
        <CustomToast
          type="error"
          title={friendlyTitle}
          description={friendlyDesc}
          closeToast={closeToast}
        />
      ),
      {
        autoClose: 5000,
        className: "custom-toast-wrapper",
        bodyClassName: "custom-toast-body",
        hideProgressBar: true,
        closeButton: false,
        icon: false
      }
    );
  },

  /**
   * Warning Toast (5s auto-dismiss)
   * @param {string} title
   * @param {string|null} description
   */
  warning: (title, description = null) => {
    return toast(
      ({ closeToast }) => (
        <CustomToast
          type="warning"
          title={title || "Warning"}
          description={description}
          closeToast={closeToast}
        />
      ),
      {
        autoClose: 5000,
        className: "custom-toast-wrapper",
        bodyClassName: "custom-toast-body",
        hideProgressBar: true,
        closeButton: false,
        icon: false
      }
    );
  },

  /**
   * Info Toast (4s auto-dismiss)
   * @param {string} title
   * @param {string|null} description
   */
  info: (title, description = null) => {
    return toast(
      ({ closeToast }) => (
        <CustomToast
          type="info"
          title={title || "Notice"}
          description={description}
          closeToast={closeToast}
        />
      ),
      {
        autoClose: 4000,
        className: "custom-toast-wrapper",
        bodyClassName: "custom-toast-body",
        hideProgressBar: true,
        closeButton: false,
        icon: false
      }
    );
  }
};

export default notify;
